import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, FileSearch, CalendarCheck, Sparkles, ArrowLeft, RefreshCw, FileText, BarChart3, Image, Globe, Leaf, Wand2, Tag, MapPin, FileSignature, MessagesSquare, Radar, FlaskConical, History } from "lucide-react";
import { getAdminStats, getAdminAudits, scoreTone } from "@/lib/api";
import { Reveal } from "@/lib/motion";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="overline">{label}</span>
        <span className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center text-brand"><Icon size={16} /></span>
      </div>
      <div className="font-heading text-4xl font-extrabold text-slate-900 mt-3">{value ?? "—"}</div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([getAdminStats(), getAdminAudits()]);
      setStats(s);
      setAudits(a);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <Link to="/" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-3"><ArrowLeft size={14} /> Back to site</Link>
            <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900">WebsiteTech Console</h1>
            <p className="text-slate-500 mt-1">Leads, audits & consultations across the acquisition funnel.</p>
          </div>
          <button onClick={load} className="btn-outline rounded-xl px-4 py-2.5 flex items-center gap-2 text-sm" data-testid="admin-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          <Link to="/admin/cms" data-testid="admin-cms-link" className="inline-flex items-center gap-2 btn-primary rounded-xl px-5 py-3">
            <FileText size={16} /> Manage Content (CMS)
          </Link>
          <Link to="/admin/crm" data-testid="admin-crm-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Users size={16} /> Lead Management (CRM)
          </Link>
          <Link to="/admin/analytics" data-testid="admin-analytics-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <BarChart3 size={16} /> Growth Analytics
          </Link>
          <Link to="/admin/clients" data-testid="admin-clients-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Users size={16} /> Client Dashboards
          </Link>
          <Link to="/admin/ai" data-testid="admin-ai-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Sparkles size={16} /> AI Agents
          </Link>
          <Link to="/admin/gallery" data-testid="admin-gallery-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Image size={16} /> Redesign Gallery
          </Link>
          <Link to="/admin/country" data-testid="admin-country-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Globe size={16} /> Country Admin (India)
          </Link>
          <Link to="/admin/kerala" data-testid="admin-kerala-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Leaf size={16} /> Kerala.com Admin
          </Link>
          <Link to="/admin/enquiries" data-testid="admin-enquiries-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <FileSearch size={16} /> Audit Enquiries
          </Link>
          <Link to="/admin/prospects" data-testid="admin-prospects-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <FileSearch size={16} /> Business Prospects
          </Link>
          <Link to="/admin/launch-chats" data-testid="admin-launch-chats-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <FileSearch size={16} /> Launch Chats
          </Link>
          <Link to="/admin/redesign" data-testid="admin-redesign-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Wand2 size={16} /> Redesign Decks
          </Link>
          <Link to="/admin/pricing" data-testid="admin-pricing-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Tag size={16} /> Service Bundles & Pricing
          </Link>
          <Link to="/admin/industry-pages" data-testid="admin-industry-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <MapPin size={16} /> Industry × Location Pages
          </Link>
          <Link to="/admin/proposals" data-testid="admin-proposals-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <FileSignature size={16} /> Advisor Proposals
          </Link>
          <Link to="/admin/demo-leads" data-testid="admin-demo-leads-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <MessagesSquare size={16} /> Demo Leads
          </Link>
          <Link to="/admin/tools" data-testid="admin-tools-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Tag size={16} /> Tools & Packages
          </Link>
          <Link to="/admin/embed" data-testid="admin-embed-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Wand2 size={16} /> Embed Assistants
          </Link>
          <Link to="/admin/whats-new" data-testid="admin-whats-new-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Sparkles size={16} /> What's New
          </Link>
          <Link to="/admin/ai-visibility" data-testid="admin-ai-visibility-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Radar size={16} /> AI-Search Visibility
          </Link>
          <Link to="/admin/growth-agent" data-testid="admin-worklog-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <Wand2 size={16} /> Growth Agent Log
          </Link>
          <Link to="/admin/experiments" data-testid="admin-experiments-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <FlaskConical size={16} /> Conversion Lab
          </Link>
          <Link to="/admin/versions" data-testid="admin-versions-link" className="inline-flex items-center gap-2 btn-outline rounded-xl px-5 py-3">
            <History size={16} /> Version History
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Leads" value={stats?.totals.leads} />
          <StatCard icon={FileSearch} label="Audits" value={stats?.totals.audits} />
          <StatCard icon={CalendarCheck} label="Consultations" value={stats?.totals.consultations} />
          <StatCard icon={Sparkles} label="Plans" value={stats?.totals.plans} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <Reveal>
            <div className="bg-white border border-slate-200 rounded-2xl card-shadow overflow-hidden">
              <div className="p-5 border-b border-slate-100"><span className="overline">Recent Leads</span></div>
              <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                {stats?.recent_leads?.length ? stats.recent_leads.map((l) => (
                  <div key={l.id} className="p-4 flex items-center justify-between" data-testid="lead-row">
                    <div>
                      <p className="font-semibold text-slate-800">{l.name}</p>
                      <p className="text-xs text-slate-400">{l.email} {l.company && `· ${l.company}`}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-semibold uppercase tracking-wide border border-slate-200 px-2 py-0.5 rounded text-slate-500">{l.source}</span>
                      {l.website_score != null && <p className="font-heading font-bold text-sm mt-1" style={{ color: scoreTone(l.website_score).color }}>{l.website_score}</p>}
                    </div>
                  </div>
                )) : <p className="p-6 text-slate-400 text-sm">No leads yet.</p>}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="bg-white border border-slate-200 rounded-2xl card-shadow overflow-hidden">
              <div className="p-5 border-b border-slate-100"><span className="overline">Recent Audits</span></div>
              <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                {audits.length ? audits.map((a) => (
                  <div key={a.id} className="p-4 flex items-center justify-between" data-testid="audit-row">
                    <div>
                      <p className="font-semibold text-slate-800 break-all">{a.domain}</p>
                      <p className="text-xs text-slate-400">{a.opportunity_count} opportunities · {new Date(a.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className="font-heading text-2xl font-extrabold" style={{ color: scoreTone(a.overall_score).color }}>{a.overall_score}</span>
                  </div>
                )) : <p className="p-6 text-slate-400 text-sm">No audits yet.</p>}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
